import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  RefreshCw,
  Trash2,
  Mail,
  Loader2,
  Info,
} from 'lucide-react';

import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/primitives/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as invitesApi from '@/api/invites';

interface InviteRow {
  id: number;
  email: string;
  tenant_id?: number | null;
  tenant_name?: string | null;
  roles: string[];
  status: string;
  expires_at?: string | null;
  sent_at?: string | null;
  opened_at?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
}

const STATUS_COLORS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  sent: 'secondary',
  opened: 'secondary',
  accepted: 'default',
  expired: 'outline',
  revoked: 'destructive',
};

/**
 * Admin view of sent invites. Lets ADMIN/FPA_ADMIN see the lifecycle
 * (sent → opened → accepted), resend stale ones, or revoke an invite the user
 * hasn't yet accepted. Creating invites now happens from the Users screen
 * (FPA Admin → Users), so this page is read-only tracking.
 *
 * FPA_ADMIN sees only their own tenant's invites; SUPER_ADMIN sees all tenants
 * filtered by the TopBar switcher.
 */
export default function InvitesAdmin() {
  const { currentUser, isSuperAdmin, isFPAAdmin, isAdmin } = useAuth();
  const { tenantId: switcherTenantId } = useTenant();

  // Effective tenant: TopBar switcher for super-admin, own tenant otherwise.
  const effectiveTenantId = isSuperAdmin()
    ? (switcherTenantId ?? currentUser?.tenant_id ?? null)
    : (currentUser?.tenant_id ?? null);

  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (effectiveTenantId == null && !isSuperAdmin()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page_size: 200 };
      if (effectiveTenantId != null) params.tenant_id = effectiveTenantId;
      const data = (await invitesApi.listInvites(params)) as unknown as {
        invites: InviteRow[];
      };
      setInvites(data?.invites ?? []);
      setError(null);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.detail || e?.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, isSuperAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleResend = async (row: InviteRow) => {
    setBusyId(row.id);
    try {
      await invitesApi.resendInvite(row.id);
      toast.success(`Invite resent to ${row.email}`);
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Resend failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (row: InviteRow) => {
    if (!window.confirm(`Revoke the invitation for ${row.email}?`)) return;
    setBusyId(row.id);
    try {
      await invitesApi.revokeInvite(row.id);
      toast.success(`Invitation to ${row.email} revoked`);
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  // Guard: non-super-admin with no tenant cannot manage invites.
  const hasNoTenant = effectiveTenantId == null && !isSuperAdmin();

  if (hasNoTenant) {
    return (
      <PageShell>
        <PageHeader title="Invitations" />
        <Alert variant="destructive">
          <AlertDescription>
            Your account isn&apos;t assigned to an FPA. Ask a super-admin to assign you before
            managing invites.
          </AlertDescription>
        </Alert>
      </PageShell>
    );
  }

  // Tenant display name for the FPA_ADMIN banner.
  const fpaName =
    invites.find((i) => i.tenant_id === effectiveTenantId)?.tenant_name ?? `FPA #${effectiveTenantId}`;

  return (
    <PageShell>
      <PageHeader
        title="Invitations"
        subtitle="Track who's been invited, resend reminders, or revoke pending invites."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={refresh}
              disabled={loading}
              className="gap-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        }
      />

      {/* FPA_ADMIN banner — shown when an FPA_ADMIN (not super-admin) is on this page */}
      {isFPAAdmin() && !isAdmin() && !isSuperAdmin() && effectiveTenantId != null && (
        <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Invitations for <strong>{fpaName}</strong>. To invite a new user, go to{' '}
            <strong>FPA Admin → Users</strong>; sent invitations are tracked here.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Section title="Pending invitations" unpadded>
        {loading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : invites.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No invitations yet"
            description="Invite users from FPA Admin → Users. Sent invitations will appear here."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((row) => {
                const isResendable = !['accepted', 'revoked'].includes(row.status);
                const isRevokable = row.status !== 'accepted';
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(row.roles || []).map((r) => (
                          <Badge key={r} variant="outline">
                            {r}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_COLORS[row.status] ?? 'outline'}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.sent_at
                        ? new Date(row.sent_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.opened_at
                        ? new Date(row.opened_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!isResendable || busyId === row.id}
                          onClick={() => handleResend(row)}
                          className="gap-1"
                        >
                          {busyId === row.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          Resend
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!isRevokable || busyId === row.id}
                          onClick={() => handleRevoke(row)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Section>

    </PageShell>
  );
}
